import { idbStorage } from '@/utils/idbStorage';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User } from '../types/auth';
import { isValidToken } from '@/utils/jwtUtils';
import { logger } from '@/utils/logger';

interface PayloadInterface {
  user?: User;
  token?: string | null;
  refresh_token?: string | null;
}

interface AuthState {
  userInfo: {
    user: User;
    token: string;
    refresh_token: string;
  };
  isHydrated: boolean;
  setUserInfo: (data: PayloadInterface) => void;
  updateUserInfo: (updates: Partial<User>) => void;
  updateUserFields: (fields: Partial<User>) => void;
  updateTokens: (token: string, refresh_token: string) => void;
  resetState: () => void;
  setHydrated: (value: boolean) => void;
  // New utility functions
  hasValidToken: () => boolean;
  hasValidRefreshToken: () => boolean;
  getToken: () => string;
  getRefreshToken: () => string;
}

const initialState = {
  userInfo: {
    user: {
      id: '',
      email: '',
      name: '',
      role: 'user' as const,
      isActive: false,
      isProfileCompleted: false,
      username: '',
      contact_number: '',
      address: '',
      image: '',
      status: 0,
      created_at: '',
      updated_at: '',
      deleted_at: null,
      is_activated: 0
    },
    token: '',
    refresh_token: ''
  },
  isHydrated: false
};

const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setUserInfo: (data: PayloadInterface) =>
          set((state: AuthState) => ({
            ...state,
            userInfo: {
              user: data.user ?? initialState.userInfo.user,
              token: data.token ?? '',
              refresh_token: data.refresh_token ?? ''
            },
            isHydrated: false
          })),

        updateUserInfo: (updates: Partial<User>) =>
          set((state: AuthState) => ({
            ...state,
            userInfo: {
              ...state.userInfo,
              user: {
                ...state.userInfo.user,
                ...updates
              }
            }
          })),

        updateUserFields: (fields: Partial<User>) =>
          set((state: AuthState) => ({
            ...state,
            userInfo: {
              ...state.userInfo,
              user: {
                ...state.userInfo.user,
                ...fields
              }
            }
          })),

        updateTokens: (token: string, refresh_token: string) =>
          set((state: AuthState) => ({
            ...state,
            userInfo: {
              ...state.userInfo,
              token,
              refresh_token
            }
          })),

        resetState: () =>
          set((state: AuthState) => ({
            ...state,
            userInfo: {
              user: initialState.userInfo.user,
              token: '',
              refresh_token: ''
            },
            isHydrated: true
          })),

        setHydrated: (value: boolean) =>
          set((state: AuthState) => ({ ...state, isHydrated: value })),

        // New utility functions
        hasValidToken: () => {
          const state = get();
          return isValidToken(state.userInfo.token);
        },

        hasValidRefreshToken: () => {
          const state = get();
          return isValidToken(state.userInfo.refresh_token);
        },

        getToken: () => {
          const state = get();
          return state.userInfo.token;
        },

        getRefreshToken: () => {
          const state = get();
          return state.userInfo.refresh_token;
        }
      }),
      {
        name: 'auth-store',
        storage: {
          getItem: async (name: string) => {
            try {
              // Strictly use secure storage (IndexedDB)
              const data = await idbStorage.getItem<{ state: AuthState }>(name);
              logger.debug('Auth store rehydration from IndexedDB', {
                name,
                hasData: !!data
              });
              return data;
            } catch (error) {
              logger.error('Failed to get auth data from storage', {
                name,
                error: error instanceof Error ? error.message : error
              });
              return null;
            }
          },
          setItem: async (name: string, value: { state: AuthState }) => {
            try {
              // Store ONLY in IndexedDB (secure)
              await idbStorage.setItem(name, value);
            } catch (error) {
              logger.error('Failed to store auth data', {
                name,
                error: error instanceof Error ? error.message : error
              });
            }
          },
          removeItem: async (name: string) => {
            try {
              // Remove from IndexedDB
              await idbStorage.removeItem(name);
            } catch (error) {
              logger.error('Failed to remove auth data', {
                name,
                error: error instanceof Error ? error.message : error
              });
            }
          }
        },
        partialize: state =>
          ({
            userInfo: state.userInfo
          }) as AuthState,
        onRehydrateStorage: () => state => {
          if (state) {
            state.setHydrated(true);
          } else {
            console.warn(
              'Auth store: onRehydrateStorage called with null state'
            );
          }
          return () => {
            if (state) {
              state.setHydrated(false);
            }
          };
        }
      }
    )
  )
);

// Optional destructure if you want named access elsewhere
export const {
  setUserInfo,
  resetState,
  updateUserInfo,
  updateUserFields,
  updateTokens,
  setHydrated,
  hasValidToken,
  hasValidRefreshToken,
  getToken,
  getRefreshToken
} = useAuthStore.getState();

export default useAuthStore;
